import { SQ_METERS_PER_SQ_MILE } from './constants';

/**
 * v23 BOLT-123 — the travel mode IS a barrier preset.
 *
 * Jake's manual method, made into an algorithm: the barriers around you (major
 * roads, water, rail) enclose a block, and that block is the play area. How you
 * are getting around decides WHICH barriers count and HOW BIG the area should
 * be, because a road you would never cross on foot is nothing in a car.
 *
 * The three mode icons are just friendly shortcuts for the barrier checklist
 * (BOLT-125): pick a mode, get its preset; open the checklist, tune it.
 *
 * Road tiers map straight onto OpenMapTiles `class`, which the tile harvest
 * already reads for portal placement. OMT lumps residential/unclassified into
 * `minor`, so the tiers fall out of a field we are already looking at.
 */
export type TravelMode = 'foot' | 'bike' | 'car';
export const TRAVEL_MODES: readonly TravelMode[] = ['foot', 'bike', 'car'];
export const DEFAULT_TRAVEL_MODE: TravelMode = 'foot';

/** Every barrier a checklist row can toggle. Roads by tier, plus the two
 *  always-on natural walls (in-phase per Jake: "reasonably incremental"). */
export type BarrierKind = 'tertiary' | 'secondary' | 'primary' | 'water' | 'rail';
export const BARRIER_KINDS: readonly BarrierKind[] = ['tertiary', 'secondary', 'primary', 'water', 'rail'];

/**
 * v25 BOLT-144 — the `kinds` sentinel for a line that is NOT a wall.
 *
 * Road names live in the `transportation_name` tile layer, which is the same
 * road geometry drawn a second time with a label on it; the `transportation`
 * layer that supplies the walls has no `name` field at all. So the composition
 * label needs that second set of lines, and the arrangement must never see them
 * or every named road would be doubled. One sentinel value in the array we
 * already send beats a second pair of parallel arrays on the wire.
 */
export const LABEL_KIND = 'label';

export function isBarrierKind(v: string): v is BarrierKind {
  return (BARRIER_KINDS as readonly string[]).includes(v);
}

/** Player-facing checklist labels (BOLT-125). */
export const BARRIER_LABEL: Record<BarrierKind, string> = {
  tertiary: 'Tertiary roads',
  secondary: 'Secondary roads',
  primary: 'Primary roads',
  water: 'Water',
  rail: 'Railway',
};

/**
 * The OMT road classes each tier contributes. "Primary" is the umbrella for
 * everything at or above it (trunk, motorway) because those are walls in every
 * mode: nobody strolls across a motorway, so they are never a checklist choice.
 */
export const TIER_CLASSES: Record<'tertiary' | 'secondary' | 'primary', readonly string[]> = {
  tertiary: ['tertiary'],
  secondary: ['secondary'],
  primary: ['primary', 'trunk', 'motorway'],
};

export interface BarrierPreset {
  /** Which barrier rows are checked for this mode. */
  barriers: BarrierKind[];
  /** How far the harvest must reach, in metres. */
  harvestRadiusM: number;
}

/**
 * The preset per mode.
 *
 * BOLT-140 REMOVED THE SIZE TARGETS. They were `areaSqMiMin/Max` per mode, and
 * measurement against real tiles killed them: at the default test location the
 * genuine foot block is 0.315 sq mi (the old floor was 0.35, so the real answer
 * was thrown away) and the genuine car block is 9.6 sq mi (the old ceiling was
 * 6.0, same). A block is the right size BECAUSE of which walls the mode keeps;
 * asking for a size on top of that only rejects true answers.
 *
 * What survives is the harvest reach. It rises with the mode for a reason
 * beyond distance: coarser modes are built from bigger roads, and OpenMapTiles
 * sheds the finer road classes as you zoom out - so the mode that must look
 * furthest is exactly the one whose walls still exist in a low-zoom tile.
 */
const PRESETS: Record<TravelMode, BarrierPreset> = {
  // Foot: every tier above residential is a wall you would rather not cross.
  foot: { barriers: ['tertiary', 'secondary', 'primary', 'water', 'rail'], harvestRadiusM: 3200 },
  // Bike: tertiary roads are nothing on a bike, so the block grows.
  bike: { barriers: ['secondary', 'primary', 'water', 'rail'], harvestRadiusM: 6400 },
  // Car: only the big roads still divide the map.
  car: { barriers: ['primary', 'water', 'rail'], harvestRadiusM: 12800 },
};

/**
 * How far the barrier harvest must actually reach for this mode.
 *
 * v25 FIELD BUG (Jake, 2026-07-29): "bicycle and car seem locked to be 1 square
 * mile". The harvest was bounded by the map VIEWPORT (`querySourceFeatures`
 * only sees loaded tiles), so past about a mile there were no lines left to
 * close a face with.
 *
 * Kept beside the presets so the harvest and the search can never disagree
 * about how much map the mode needs.
 */
export function requiredCoverageM(mode: TravelMode): number {
  return PRESETS[mode].harvestRadiusM;
}

/**
 * The search windows, smallest first, for this mode.
 *
 * The SCHEDULE is shared; only how far it is allowed to run differs. That is
 * what makes "foot <= bike <= car" a theorem rather than a tuning accident: the
 * barrier sets are nested (foot keeps every wall bike does, and more), so at a
 * given window a coarser mode's face can only be the same or a merger of
 * several. Since a coarser mode also harvests FURTHER, it can never fail where
 * a finer one succeeded.
 */
export const SEARCH_WINDOWS_M: readonly number[] = [800, 1600, 3200, 6400, 12800];

export function searchWindowsFor(mode: TravelMode): number[] {
  return SEARCH_WINDOWS_M.filter((r) => r <= PRESETS[mode].harvestRadiusM);
}

export function barrierPresetFor(mode: TravelMode): BarrierPreset {
  const p = PRESETS[mode];
  return { ...p, barriers: [...p.barriers] };
}

/** The road classes an active barrier set treats as walls. */
export function roadClassesFor(barriers: readonly BarrierKind[]): Set<string> {
  const out = new Set<string>();
  for (const b of barriers) {
    if (b === 'tertiary' || b === 'secondary' || b === 'primary') {
      for (const c of TIER_CLASSES[b]) out.add(c);
    }
  }
  return out;
}

/** Accessibility labels for the icon-only picker (BOLT-123: icons, no text). */
export const TRAVEL_MODE_LABEL: Record<TravelMode, string> = {
  foot: 'Walking',
  bike: 'Biking',
  car: 'Driving',
};

/** Play-area size, for the label under the map. */
export function areaSqMi(areaSqMeters: number): number {
  return areaSqMeters / SQ_METERS_PER_SQ_MILE;
}

/**
 * v23 — AUTO vs CUSTOM, so nothing the player drew ever gets stomped.
 *
 * AUTO: the boundary is algorithm-generated, so a mode or checklist change
 * regenerates it. CUSTOM: the boundary is the user's (drawn, or a reused recent
 * map), so a mode change must NOT overwrite it; we offer to regenerate instead.
 * The user is never surprised by a redraw they did not ask for.
 */
export type BoundaryOrigin = 'auto' | 'custom';

export function shouldRegenerateOnModeChange(origin: BoundaryOrigin): boolean {
  return origin === 'auto';
}

export function regenerateOfferFor(mode: TravelMode): string {
  return `Regenerate for ${TRAVEL_MODE_LABEL[mode].toLowerCase()}?`;
}
